import { ChevronDown, ChevronUp } from "./central-icons";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

import { central, spacing } from "../theme/tokens";

export type CentralDataGridColumn<Row> = {
  header: string;
  key: string;
  priority: 1 | 2 | 3;
  render: (row: Row) => ReactNode;
  sortValue?: (row: Row) => number | string;
  width?: number;
};

export function CentralDataGrid<Row>({
  columns,
  compactBreakpoint = 720,
  defaultSort,
  emptyDescription,
  emptyTitle,
  getRowId,
  getRowAccessibilityLabel,
  label,
  onActivateRow,
  rows,
  stateKey,
}: {
  columns: readonly CentralDataGridColumn<Row>[];
  compactBreakpoint?: number;
  defaultSort?: { direction: "asc" | "desc"; key: string };
  emptyDescription: string;
  emptyTitle: string;
  getRowId: (row: Row) => string;
  getRowAccessibilityLabel?: (row: Row) => string;
  label: string;
  onActivateRow?: (row: Row) => void;
  rows: readonly Row[];
  stateKey: string;
}) {
  const { width } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const compact = (containerWidth ?? width) < compactBreakpoint;
  const [sort, setSort] = useState<{ direction: "asc" | "desc"; key: string }>(() =>
    readPersistedSort(stateKey, columns, defaultSort),
  );

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    window.localStorage.setItem(
      `central-grid:${stateKey}:sort`,
      JSON.stringify(sort),
    );
  }, [sort, stateKey]);

  const sortedRows = useMemo(() => {
    const column = columns.find((candidate) => candidate.key === sort.key);
    if (!column?.sortValue) return [...rows];
    return [...rows].sort((left, right) => {
      const leftValue = column.sortValue?.(left) ?? "";
      const rightValue = column.sortValue?.(right) ?? "";
      const result =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), undefined, {
              numeric: true,
              sensitivity: "base",
            });
      return sort.direction === "asc" ? result : -result;
    });
  }, [columns, rows, sort]);

  const sortableColumns = columns.filter((column) => column.sortValue);
  const updateSort = (key: string) => {
    setSort((current) => ({
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
      key,
    }));
  };

  if (!sortedRows.length) {
    return (
      <View accessibilityLiveRegion="polite" style={styles.empty}>
        <Text style={styles.emptyTitle}>{emptyTitle}</Text>
        <Text style={styles.emptyDescription}>{emptyDescription}</Text>
      </View>
    );
  }

  if (compact) {
    const compactColumns = columns.filter((column) => column.priority <= 2);
    return (
      <View
        accessibilityLabel={label}
        onLayout={(event) => setContainerWidth(event.nativeEvent.layout.width)}
        style={styles.compactGrid}
      >
        {sortableColumns.length ? (
          <View accessibilityLabel="Sort options" style={styles.sortBar}>
            <Text style={styles.sortLabel}>Sort by</Text>
            <ScrollView
              contentContainerStyle={styles.sortOptions}
              horizontal
              showsHorizontalScrollIndicator={false}
            >
              {sortableColumns.map((column) => {
                const active = column.key === sort.key;
                const direction = active ? sort.direction : null;
                return (
                  <Pressable
                    accessibilityLabel={`Sort by ${column.header}${active ? `, currently ${direction === "asc" ? "ascending" : "descending"}` : ""}`}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    key={column.key}
                    onPress={() => updateSort(column.key)}
                    style={({ pressed }) => [
                      styles.sortOption,
                      active ? styles.sortOptionActive : null,
                      pressed ? styles.sortOptionPressed : null,
                    ]}
                  >
                    <Text style={[styles.sortOptionText, active ? styles.sortOptionTextActive : null]}>
                      {column.header}
                    </Text>
                    {active ? (
                      direction === "asc" ? (
                        <ChevronUp color={central.action.primary} size={14} />
                      ) : (
                        <ChevronDown color={central.action.primary} size={14} />
                      )
                    ) : null}
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
        ) : null}
        <View style={styles.cards}>
          {sortedRows.map((row) => {
            const rowId = getRowId(row);
            return (
              <Pressable
                accessibilityHint={onActivateRow ? "Opens this work item" : undefined}
                accessibilityLabel={onActivateRow ? getRowAccessibilityLabel?.(row) : undefined}
                accessibilityRole={onActivateRow ? "button" : undefined}
                key={rowId}
                onPress={onActivateRow ? () => onActivateRow(row) : undefined}
                style={({ pressed }) => [
                  styles.cardRow,
                  pressed && onActivateRow ? styles.rowPressed : null,
                ]}
              >
                {compactColumns.map((column, index) => (
                  <View
                    key={column.key}
                    style={index === 0 ? styles.cardPrimary : styles.cardField}
                  >
                    {index === 0 ? null : (
                      <Text style={styles.cardLabel}>{column.header}</Text>
                    )}
                    <View style={styles.cardValue}>{column.render(row)}</View>
                  </View>
                ))}
              </Pressable>
            );
          })}
        </View>
      </View>
    );
  }

  return (
    <ScrollView horizontal onLayout={(event) => setContainerWidth(event.nativeEvent.layout.width)} showsHorizontalScrollIndicator>
      <View
        accessibilityLabel={label}
        accessibilityRole={"grid" as never}
        style={styles.grid}
      >
        <View accessibilityRole={"row" as never} style={[styles.row, styles.headerRow]}>
          {columns.map((column) => {
            const active = column.key === sort.key;
            const ariaSort = active
              ? sort.direction === "asc"
                ? "ascending"
                : "descending"
              : "none";
            return (
              <Pressable
                accessibilityLabel={
                  column.sortValue
                    ? `Sort by ${column.header}${active ? `, currently ${ariaSort}` : ""}`
                    : column.header
                }
                accessibilityRole={"columnheader" as never}
                disabled={!column.sortValue}
                key={column.key}
                onPress={() => {
                  if (!column.sortValue) return;
                  updateSort(column.key);
                }}
                {...(Platform.OS === "web" ? ({ "aria-sort": ariaSort } as never) : {})}
                style={[styles.cell, { width: column.width ?? 180 }]}
              >
                <Text style={styles.headerText}>{column.header}</Text>
                {active && column.sortValue ? (
                  sort.direction === "asc" ? (
                    <ChevronUp color={central.text.muted} size={14} />
                  ) : (
                    <ChevronDown color={central.text.muted} size={14} />
                  )
                ) : null}
              </Pressable>
            );
          })}
        </View>
        {sortedRows.map((row) => {
          const rowId = getRowId(row);
          return (
            <Pressable
              accessibilityHint={onActivateRow ? "Opens this work item" : undefined}
              accessibilityLabel={onActivateRow ? getRowAccessibilityLabel?.(row) : undefined}
              accessibilityRole={"row" as never}
              key={rowId}
              onPress={onActivateRow ? () => onActivateRow(row) : undefined}
              style={({ pressed }) => [
                styles.row,
                pressed && onActivateRow ? styles.rowPressed : null,
              ]}
              {...(Platform.OS === "web" && onActivateRow ? ({ tabIndex: 0 } as never) : {})}
            >
              {columns.map((column) => (
                <View
                  accessibilityRole={"gridcell" as never}
                  key={column.key}
                  style={[styles.cell, { width: column.width ?? 180 }]}
                >
                  {column.render(row)}
                </View>
              ))}
            </Pressable>
          );
        })}
      </View>
    </ScrollView>
  );
}

function readPersistedSort<Row>(
  stateKey: string,
  columns: readonly CentralDataGridColumn<Row>[],
  defaultSort?: { direction: "asc" | "desc"; key: string },
): { direction: "asc" | "desc"; key: string } {
  const fallbackColumn = columns.find((column) => column.sortValue);
  const validDefault = defaultSort
    && columns.some((column) => column.key === defaultSort.key && column.sortValue);
  const fallback = validDefault
    ? defaultSort
    : {
        direction: "asc" as const,
        key: fallbackColumn?.key ?? columns[0]?.key ?? "",
      };
  if (Platform.OS !== "web" || typeof window === "undefined") return fallback;
  try {
    const value = JSON.parse(
      window.localStorage.getItem(`central-grid:${stateKey}:sort`) ?? "null",
    ) as { direction?: unknown; key?: unknown } | null;
    if (
      value &&
      (value.direction === "asc" || value.direction === "desc") &&
      columns.some((column) => column.key === value.key && column.sortValue)
    ) {
      return { direction: value.direction, key: String(value.key) };
    }
  } catch {
    // Ignore malformed browser state and return the stable default.
  }
  return fallback;
}

const styles = StyleSheet.create({
  cardField: { flexBasis: "100%", gap: spacing.xxs, minWidth: 0 },
  cardLabel: {
    color: central.text.muted,
    fontFamily: central.typography.label,
    fontSize: 11,
  },
  cardPrimary: { flexBasis: "100%" },
  cardRow: {
    backgroundColor: central.surface.raised,
    borderBottomColor: central.border.subtle,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
    minHeight: 72,
    padding: spacing.md,
  },
  cardValue: { minHeight: 20 },
  cards: {
    borderColor: central.border.default,
    borderRadius: central.radius.card,
    borderWidth: 1,
    overflow: "hidden",
  },
  compactGrid: { gap: spacing.sm },
  cell: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xs,
    minHeight: 48,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  empty: {
    alignItems: "flex-start",
    backgroundColor: central.surface.muted,
    borderRadius: central.radius.card,
    gap: spacing.xs,
    padding: spacing.lg,
  },
  emptyDescription: {
    color: central.text.secondary,
    fontFamily: central.typography.body,
    fontSize: 14,
  },
  emptyTitle: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 16,
  },
  grid: {
    borderColor: central.border.default,
    borderRadius: central.radius.card,
    borderWidth: 1,
    minWidth: 880,
    overflow: "hidden",
  },
  headerRow: { backgroundColor: central.surface.muted },
  headerText: {
    color: central.text.secondary,
    fontFamily: central.typography.label,
    fontSize: 12,
  },
  row: {
    backgroundColor: central.surface.raised,
    borderBottomColor: central.border.subtle,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: "row",
  },
  rowPressed: { backgroundColor: central.action.selected },
  sortBar: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.sm,
    minWidth: 0,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  sortLabel: {
    color: central.text.secondary,
    flexShrink: 0,
    fontFamily: central.typography.label,
    fontSize: 12,
  },
  sortOption: {
    alignItems: "center",
    borderColor: central.border.default,
    borderRadius: central.radius.control,
    borderWidth: 1,
    flexDirection: "row",
    gap: spacing.xxs,
    minHeight: 44,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  sortOptionActive: {
    backgroundColor: central.action.selected,
    borderColor: central.action.primary,
  },
  sortOptionPressed: { opacity: 0.72 },
  sortOptionText: {
    color: central.text.secondary,
    fontFamily: central.typography.label,
    fontSize: 12,
  },
  sortOptionTextActive: { color: central.action.primary },
  sortOptions: { gap: spacing.xs, paddingRight: spacing.xs },
});
